/**
 * DecisionsSection (FLOW-572)
 *
 * Read-only list of decisions linked to a work item (FLOW-571 links),
 * grouped by relationship. Decision authoring, linking, and unlinking are
 * out of scope — see `DecisionQuickCapture` (FLOW-570) and the work item
 * decision link endpoints for those.
 *
 * Modelled on `LinksSection`, minus the add/remove affordances: this section
 * only ever reads. Renders nothing when there are no linked decisions,
 * since most work items won't have any for a long while — but still
 * surfaces a fetch error rather than silently blanking.
 */

import {
  Component,
  For,
  Show,
  createMemo,
  createResource,
  createSignal,
} from "solid-js";
import { getDecision, listWorkItemDecisions } from "../../api/decisions";
import type {
  DecisionRelationship,
  ProjectId,
  WorkItemDecisionLink,
  WorkItemId,
} from "../../api/types";
import {
  DECISION_CATEGORY_BADGE_COLOR,
  decisionCategoryLabel,
  decisionStatusColor,
  decisionStatusLabel,
} from "../../utils/decisionDisplay";

export interface DecisionsSectionProps {
  workItemId: WorkItemId;
  projectId: ProjectId;
}

const GROUP_ORDER: { relationship: DecisionRelationship; label: string }[] = [
  { relationship: "implements", label: "Implements" },
  { relationship: "prompted_by", label: "Prompted by" },
  { relationship: "affected_by", label: "Affected by" },
  { relationship: "produced", label: "Produced" },
];

const DecisionRow: Component<{
  link: WorkItemDecisionLink;
  projectId: ProjectId;
}> = (props) => {
  const [expanded, setExpanded] = createSignal(false);

  const [decision] = createResource(
    () =>
      expanded()
        ? ([props.projectId, props.link.decision.id] as const)
        : undefined,
    ([projectId, id]) => getDecision(projectId, id),
  );

  return (
    <li class="py-2">
      <button
        type="button"
        class="w-full text-left flex items-start gap-2 hover:bg-sunken rounded px-1 -mx-1 py-0.5 focus-ring"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded()}
      >
        <svg
          class={
            "w-4 h-4 mt-0.5 text-ink-3 flex-shrink-0 transition-transform " +
            (expanded() ? "rotate-90" : "")
          }
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-sm text-ink">{props.link.decision.title}</span>
            <span
              class={
                "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
                DECISION_CATEGORY_BADGE_COLOR
              }
            >
              {decisionCategoryLabel(props.link.decision.category)}
            </span>
            <span
              class={
                "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
                decisionStatusColor(props.link.decision.status)
              }
            >
              {decisionStatusLabel(props.link.decision.status)}
            </span>
          </div>
          <Show when={expanded()}>
            <div class="mt-2 text-sm text-ink-2">
              <Show
                when={!decision.loading}
                fallback={<span class="text-ink-3">Loading…</span>}
              >
                <Show
                  when={!decision.error}
                  fallback={
                    <span class="text-signal-red">
                      Couldn't load this decision's rationale.
                    </span>
                  }
                >
                  <p class="whitespace-pre-wrap">{decision()?.rationale}</p>
                </Show>
              </Show>
            </div>
          </Show>
        </div>
      </button>
    </li>
  );
};

export const DecisionsSection: Component<DecisionsSectionProps> = (props) => {
  const [data] = createResource(() => props.workItemId, listWorkItemDecisions);

  const groups = createMemo(() => {
    // Guard on `data.state`, not `data()` directly: the resource accessor
    // throws the stored error when read in the "errored" state (and
    // `.latest` does too, once there's no pending refetch), which would
    // abort this memo's computation mid-flight and leave the `data.error`
    // Show below never rendering.
    const links = data.state === "errored" ? [] : (data()?.links ?? []);
    return GROUP_ORDER.map((g) => ({
      label: g.label,
      items: links.filter((l) => l.relationship === g.relationship),
    })).filter((g) => g.items.length > 0);
  });

  const hasContent = createMemo(() => groups().length > 0);

  return (
    <>
      <Show when={data.error}>
        <div class="bg-surface rounded border border-signal-red/25 p-4">
          <h3 class="text-sm font-medium text-ink-2 mb-2">Decisions</h3>
          <p class="text-sm text-signal-red">Couldn't load linked decisions.</p>
        </div>
      </Show>
      <Show when={!data.error && hasContent()}>
        <div class="bg-surface rounded border border-line p-4">
          <h3 class="text-sm font-medium text-ink-2 mb-3">Decisions</h3>
          <For each={groups()}>
            {(group) => (
              <div class="mb-3 last:mb-0">
                <h4 class="text-xs font-medium text-ink-3 mb-1">
                  {group.label}
                </h4>
                <ul class="divide-y divide-line">
                  <For each={group.items}>
                    {(link) => (
                      <DecisionRow link={link} projectId={props.projectId} />
                    )}
                  </For>
                </ul>
              </div>
            )}
          </For>
        </div>
      </Show>
    </>
  );
};

export default DecisionsSection;
